import { Box, Divider, Link, Paper, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useGetMany, useGetOne } from "react-admin";
import { Client, DocumentRead, Policy, ResourceName, User, Vendor } from "@/types";
import { BORDER_RADIUS } from "@/constants/layout";
import { getCarrierLabel } from "@/components/common/PolicyField";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { formatDocumentCategory } from "../../utils/documentUtils";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { acknowledgementLabel } from "../../utils/acknowledgementLabel";
import { resolvedPolicyLabel } from "../document-review/ReviewContextBar";
import { useDocumentTransformationData } from "../../hooks/useDocumentTransformationData";
import { useDocumentClassification } from "../../hooks/useDocumentClassification";
import { vendorTypeLabel } from "../DocumentInFlightPanel";

export interface ContextCell {
  label: string;
  value: string;
  /** Internal route when the value is a record. */
  to?: string;
}

/**
 * The cells, in the review header's order, with unknowns omitted (#7550 AC1).
 * Pure so the omission rules are testable.
 */
export const buildResultContextCells = ({
  client,
  policy,
  vendor,
  category,
  layout,
  uploadedAt,
  uploadedBy,
  loadedAt,
  loadedBy,
}: {
  client: Client | undefined;
  policy: Policy | undefined;
  vendor: Vendor | undefined;
  category: string | undefined;
  layout: string | undefined;
  uploadedAt?: string;
  uploadedBy?: string;
  loadedAt: string | undefined;
  loadedBy: string | undefined;
}): ContextCell[] => {
  const cells: ContextCell[] = [];
  if (client) {
    cells.push({
      label: "Client",
      value: client.client_legal_name || client.client_short_name || "Client",
      to: `/${ResourceName.CLIENTS}/${client.id}/show`,
    });
  }
  if (policy) {
    const label = resolvedPolicyLabel({
      policy_id: String(policy.id),
      aegle_id: policy.aegle_id ?? "",
      carrier_name: getCarrierLabel(policy),
      effective_start_date: policy.effective_start_date,
      effective_end_date: policy.effective_end_date,
    });
    if (label) cells.push({ label: "Policy", value: label, to: `/${ResourceName.POLICIES}/${policy.id}/show` });
  }
  if (vendor) {
    const type = vendorTypeLabel(vendor);
    cells.push({
      label: "Administrator",
      value: [vendor.company_name, type].filter(Boolean).join(" · "),
      to: `/${ResourceName.VENDORS}/${vendor.id}/show`,
    });
  }
  // Classification values are machine keys ("stoploss_claims"); label them the way the app does.
  if (category) cells.push({ label: "Category", value: formatDocumentCategory(category) });
  if (layout) cells.push({ label: "Layout", value: layout });
  if (uploadedAt || uploadedBy) {
    cells.push({ label: "Uploaded", value: [uploadedAt, uploadedBy].filter(Boolean).join(" · ") });
  }
  if (loadedAt || loadedBy) {
    cells.push({ label: "Loaded", value: [loadedAt, loadedBy].filter(Boolean).join(" · ") });
  }
  return cells;
};

/**
 * Read-only band above the post-load table (#7550 AC1): the review header's
 * cells as plain text with links where a record exists, plus when and by whom
 * the file was loaded. Nothing here is editable; a cell whose value is
 * unknown is omitted.
 *
 * Policy is the term the load ran against (resolveLoadPolicy: the term this
 * load's lines carry, else the client's newest), passed in by the result view so
 * the bar and the rows agree. Paid through is omitted: the document does not
 * store the reviewer's as-of date yet.
 */
export const ResultContextBar = ({ documentRecord, policy }: { documentRecord: DocumentRead; policy?: Policy }) => {
  const clientId = documentRecord.client_id_ref;
  const vendorId = documentRecord.vendor_id_ref;
  const { data: client } = useGetOne<Client>(ResourceName.CLIENTS, { id: clientId ?? "" }, { enabled: !!clientId });
  const { data: vendor } = useGetOne<Vendor>(ResourceName.VENDORS, { id: vendorId ?? "" }, { enabled: !!vendorId });
  const { latestTransformation, template } = useDocumentTransformationData({ documentId: String(documentRecord.id) });

  const acks = documentRecord.review_acknowledgements ?? [];
  const reviewerId = acks.map((a) => a.user_id).find((u): u is string => !!u);
  const uploaderId = documentRecord.uploaded_by_user_id_ref;
  const userIds = Array.from(new Set([reviewerId, uploaderId].filter((u): u is string => !!u)));
  const { data: users } = useGetMany<User>(ResourceName.USERS, { ids: userIds }, { enabled: userIds.length > 0 });
  const nameFor = (id: string | undefined): string | undefined => {
    const u = id ? users?.find((x) => String(x.id) === id) : undefined;
    return u ? u.full_name || u.preferred_name || u.email : undefined;
  };
  const reviewerName = nameFor(reviewerId);

  const { classification } = useDocumentClassification({ documentId: String(documentRecord.id), enabled: true });
  const category = classification?.effective || classification?.auto_classified || undefined;
  const cells = buildResultContextCells({
    client,
    policy,
    vendor,
    category: category || undefined,
    layout: latestTransformation ? (template?.name ?? "Automatic extraction") : undefined,
    loadedAt: documentRecord.processed_datetime
      ? formatDate(String(documentRecord.processed_datetime), DATE_FORMATS.DISPLAY_LONG_WITH_TIME)
      : undefined,
    uploadedAt: documentRecord.uploaded_datetime
      ? formatDate(String(documentRecord.uploaded_datetime), DATE_FORMATS.DISPLAY_LONG)
      : undefined,
    uploadedBy: nameFor(uploaderId),
    loadedBy: reviewerName,
  });

  if (cells.length === 0 && acks.length === 0) return null;

  return (
    <Paper
      elevation={0}
      data-testid="result-context-bar"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        columnGap: 3.5,
        rowGap: 1,
        px: 2,
        py: 1.5,
        mb: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: BORDER_RADIUS.LARGE,
      }}
    >
      {cells.map((cell) => (
        <Box key={cell.label} sx={{ minWidth: 0 }}>
          <Typography component="div" variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {cell.label}
          </Typography>
          {cell.to ? (
            <Link
              component={RouterLink}
              to={cell.to}
              variant="body2"
              underline="hover"
              // Quiet link: the records are context, not the point of the page.
              sx={{ fontWeight: 500, color: "text.primary", textDecorationColor: "divider" }}
            >
              {cell.value}
            </Link>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: cell.label === "Loaded" ? 400 : 500 }}>
              {cell.value}
            </Typography>
          )}
        </Box>
      ))}
      {acks.length > 0 && (
        <>
          <Divider sx={{ width: "100%", my: 0.5 }} />
          {/* What the reviewer confirmed before this loaded (#7550 AC9), with the
              undo rule behind the hint rather than in prose. */}
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            columnGap={2}
            rowGap={0.5}
            sx={{ width: "100%" }}
            data-testid="review-record"
          >
            <Stack direction="row" alignItems="center" columnGap={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Reviewed
              </Typography>
              <HintIcon hint="Undo requires an admin to delete this document." />
            </Stack>
            {acks.map((a, i) => {
              const label = acknowledgementLabel(a);
              const detail = a.detail && a.detail !== label ? a.detail : null;
              return (
                <Stack key={`${a.check}-${i}`} direction="row" alignItems="center" columnGap={0.5}>
                  <Typography
                    component="span"
                    sx={{ color: "success.main", fontWeight: 700, fontSize: 13 }}
                    aria-hidden="true"
                  >
                    ✓
                  </Typography>
                  <Typography variant="caption">{detail ? `${label} · ${detail}` : label}</Typography>
                </Stack>
              );
            })}
            {reviewerName && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                by {reviewerName}
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
};
